/**
 * EditorPlaybackBar — the shared bottom playback bar used by every editor.
 *
 * Matches the Synth VR (SrPlaybackBar) layout:
 *   [ transport pill: rewind · play · timer · spectrum ] [ waveform (centre) ] [ song info ]
 * with an optional hint row sitting above the waveform column. The note-count /
 * beat readout was dropped; the song cover + name + artist live in the right pill,
 * and each editor injects its own 48px-tall timeline as the `waveform` slot.
 *
 * Fully prop-driven so each editor wires its own store/audio engine.
 */

import React from 'react';
import { transportStyle, transportBtn } from './editorSharedStyles';
import { color, font } from './theme';
import { IcTriRight } from './icons';
import SongTag from './SongTag';

interface Props {
  playing: boolean;
  onTogglePlay: () => void;
  onRewind: () => void;
  currentTime: number;
  duration: number;
  coverUrl: string | null;
  /** Bold top line — the song / track name. */
  primary: string;
  /** Smaller second line — the artist(s). */
  secondary: string;
  onSongClick?: () => void;
  /** Centre column — the editor's own 48px-tall timeline / waveform. */
  waveform: React.ReactNode;
  /** Live spectrum widget shown inside the transport pill (optional). */
  spectrum?: React.ReactNode;
  /** Hint / help row shown above the waveform column (optional). */
  hint?: React.ReactNode;
}

const fmtTime = (s: number) => {
  const mm = Math.floor(s / 60);
  const ss = Math.floor(s % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
};

export default function EditorPlaybackBar({
  playing, onTogglePlay, onRewind, currentTime, duration,
  coverUrl, primary, secondary, onSongClick, waveform, spectrum, hint,
}: Props) {
  return (
    // 3-column grid (transport · waveform · song). The hint sits in row 1 of the
    // middle column so it's exactly the waveform's width and position.
    // Grid template + narrow-viewport breakpoints live in App.css (.sr-playbar
    // — shared with the VR bar).
    <div className="sr-playbar">
      {/* Row 1 — hint aligned to the waveform column. The cell clamps the hint
          to a single clipped line (narrow centre columns used to wrap it into a
          ~200px-tall stack) and hides it on very narrow viewports. */}
      {hint && (<>
        <div />
        <div className="sr-hint-cell">{hint}</div>
        <div />
      </>)}

      {/* Row 2, col 1 — Transport. Small right padding so the spectrum pill
          nestles into the container's right curve. */}
      <div className="sr-pb-pill" style={{ ...barPillStyle, paddingLeft: 6, paddingRight: 4 }}>
        <button
          onClick={(e) => { (e.currentTarget as HTMLButtonElement).blur(); onRewind(); }}
          style={transportBtnRound}
          title="Rewind"
        >⏮</button>
        <button
          onClick={(e) => { (e.currentTarget as HTMLButtonElement).blur(); onTogglePlay(); }}
          style={transportBtnRound}
          className={playing ? 'accent-fill-active' : undefined}
          title="Play / Pause"
        >{playing ? '❚❚' : <IcTriRight size={14} />}</button>
        <span style={{ ...timeReadoutStyle, flex: 1, textAlign: 'center' }}>
          {fmtTime(currentTime)} / {fmtTime(duration)}
        </span>
        {spectrum && <div className="sr-viz-pill" style={vizPillStyle}>{spectrum}</div>}
      </div>

      {/* Row 2, col 2 — Waveform (fills the centre; clips fixed-width edge
          controls on very narrow viewports instead of spilling off-screen) */}
      <div style={{ minWidth: 0, display: 'flex', overflow: 'hidden' }}>
        {waveform}
      </div>

      {/* Row 2, col 3 — Song info. Small left padding so the round vinyl cover
          nestles into the pill's left curve. */}
      <div className="sr-pb-pill sr-song-pill" style={{ ...barPillStyle, paddingLeft: 6 }}>
        <SongTag bare spinning={playing} coverUrl={coverUrl} primary={primary} secondary={secondary} duration={0} onClick={onSongClick} />
      </div>
    </div>
  );
}

const barPillStyle: React.CSSProperties = {
  ...transportStyle,
  borderRadius: 999,
  gap: 10,
  height: 48,
  padding: '0 16px',
  alignItems: 'center',
};

const transportBtnRound: React.CSSProperties = {
  ...transportBtn,
  width: 36,
  height: 36,
  padding: 0,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const timeReadoutStyle: React.CSSProperties = {
  color: color.ink,
  fontFamily: font.sans,
  fontSize: 11,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};

// Live-spectrum pill on the right of the transport — same sizing as VR
// (height of the play button, width of the two buttons together).
const vizPillStyle: React.CSSProperties = {
  flexShrink: 0,
  width: 82,
  height: 36,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 999,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)',
};
